import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { ChartBindingValue, CustomChartPoint } from '@/entities/dashboards';
import type { QualityZoneRanges } from '@/shared/lib';
import { ChartCardShell } from '../ChartCardShell';
import { chartElementColorClass, type ChartAppearance } from '../chartElements';
import { ChartPointTooltip } from './ChartPointTooltip';
import { CHART_AXIS_TICK, CHART_LINE_COLOR, CHART_SERIES_COLOR, formatAxisValue } from './chartAxis';
import { ChartLegend } from './ChartLegend';
import { valueRuleLegendItems } from './chartLegendItems';

type Props = {
  title: string;
  points: CustomChartPoint[];
  /** Пороги зон качества пользователя: ими окрашена отметка со своим потолком */
  zoneRanges: QualityZoneRanges;
  /** Оформление диаграммы: правила раскраски отметок по величине */
  appearance?: ChartAppearance;
  /** Выбор периода наполняет связанную диаграмму; не задан — периоды не выбираются */
  onSelectElement?: (value: ChartBindingValue) => void;
};

/** Ширина оси величины: хватает подписи с дробной частью, но не отнимает ширину у самой линии. */
const VALUE_AXIS_WIDTH = 44;

/** Величина отметки периода и её же величина под курсором. */
const POINT_SIZE = 3;
const ACTIVE_POINT_SIZE = 5;

/**
 * Линейный график: изменение величины по периодам.
 *
 * Точка без значения разрывает линию — соединять соседние периоды через пропуск нельзя, это
 * рисовало бы динамику, которой не было.
 *
 * Ось величины идёт от наименьшего значения ряда, а не от нуля: у показателей с узким размахом
 * (средний балл, доля) ход по периодам на шкале от нуля сплющился бы в прямую. Сама ось остаётся:
 * у отметок величина не подписана, и без оси её было бы не прочитать.
 *
 * Отметка окрашивается правилами величины, если они заданы: что означает цвет, названо легендой
 * под рисунком.
 */
export const LineChartView: FC<Props> = ({ title, points, zoneRanges, appearance, onSelectElement }) => {
  const { t } = useTranslation('qualityDashboard');

  const legendItems = useMemo(() => valueRuleLegendItems(appearance?.valueColorRules, t), [appearance, t]);

  return (
    <ChartCardShell title={title}>
      <div className="flex flex-col">
        <div className="h-48 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={points}
              // Выбор идёт по всему графику, а не по самой отметке: попасть в отметку в несколько
              // точек шириной тяжело, а библиотека и так знает, к какому периоду ближе указатель.
              onClick={
                onSelectElement
                  ? (state: { activeLabel?: string | number }) =>
                      state.activeLabel === undefined ? undefined : onSelectElement(String(state.activeLabel))
                  : undefined
              }
              className={onSelectElement ? 'cursor-pointer' : undefined}
            >
              <CartesianGrid stroke={CHART_LINE_COLOR} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tick={CHART_AXIS_TICK}
                tickLine={false}
                axisLine={{ stroke: CHART_LINE_COLOR }}
                interval="preserveStartEnd"
              />
              <YAxis
                width={VALUE_AXIS_WIDTH}
                domain={['auto', 'auto']}
                tickFormatter={formatAxisValue}
                tick={CHART_AXIS_TICK}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<ChartPointTooltip />} cursor={{ stroke: CHART_LINE_COLOR }} />
              <Line
                type="linear"
                dataKey="value"
                stroke={CHART_SERIES_COLOR}
                // Отметка рисуется своим кружком, а не готовым: цвет ей достаётся по величине, а
                // готовая отметка красится одна на весь ряд.
                dot={({ cx, cy, payload }) => {
                  const point = payload as CustomChartPoint | undefined;
                  if (cx === undefined || cy === undefined || !point) return <g />;
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={POINT_SIZE}
                      fill={CHART_SERIES_COLOR}
                      className={chartElementColorClass(point, appearance, zoneRanges)}
                    />
                  );
                }}
                activeDot={{ r: ACTIVE_POINT_SIZE }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <ChartLegend items={legendItems} />
      </div>
    </ChartCardShell>
  );
};
